import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import moment from "moment";
import Select from "../../Common/FormElements/Select/Select";
import TextInput from "../../Common/FormElements/TextInput/TextInput";
import TextArea from "../../Common/FormElements/TextArea/TextArea";
import CalendarInput from "../../Common/FormElements/CalendarInput/CalendarInput";
import Button from "../../Common/FormElements/Button/Button";
import Separator from "../../Common/Separator/Separator";
import VerticalPoints from "../../Common/VerticalPoints/VerticalPoints";
import ConfirmModal from "../../Common/Modal/ConfirmModal";
import ActionIcon from "../../Prospects/NewProspect/Actions/ActionIcon";
import { projectStatusLabel, projectStatusOptions } from "./projectStatuses";
import {
  getActionFormTitle,
  getProspectActionTypeOptions,
  getValidTypeForActionStatus,
} from "../../../Utils/prospect";
import {
  createUpdateProjectAction,
  convertProjectReminderToAction,
  deleteProjectAction,
  updateProject,
} from "../../../REDUX/actions/appProjects";
import {
  openCenteredModal,
  closeCenteredModal,
} from "../../../REDUX/actions/centeredModal";

/** Slide-12 tunnel — same mechanics as the partner-prospect pipeline, with
 * the prospect statuses/actions reused until the client's Word doc arrives. */
const ProjectActionForm = ({ projectId, status, action, data, handleClose }) => {
  const UPDATE = Boolean(data);
  const { t } = useTranslation("common");
  const dispatch = useDispatch();
  const [formData, setFormData] = useState(
    data
      ? {
          id: data.id,
          description: data.description ?? "",
          comment: data.comment ?? "",
          date: data.date ? new Date(data.date) : undefined,
          type: getValidTypeForActionStatus(data.status ?? status, data.type, t),
        }
      : {}
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const isValid =
    formData.type && (action !== "REMINDER" || Boolean(formData.date));

  const handleSubmit = () => {
    dispatch(
      createUpdateProjectAction(
        { ...formData },
        action === "REMINDER",
        status,
        projectId,
        t
      )
    );
    handleClose();
  };

  return (
    <div className={`${!UPDATE ? "p-4 bg-dimGrey" : "p-4 bg-white br10"} position-relative`}>
      <p className="text-uppercase text-dark mb-4">
        {getActionFormTitle(action, UPDATE, t)}
      </p>
      <div className="row g-4">
        <div className="col-6">
          <Select
            name="type"
            label={t("Action")}
            placeholder={t("Action")}
            options={getProspectActionTypeOptions(status, action, t)}
            value={formData.type}
            callback={handleChange}
            showClear
            resetable
            appendToSelf
          />
        </div>
        <div className="col-6">
          <TextInput
            name="description"
            label={t("Description")}
            placeholder={t("Description")}
            maxLength={35}
            value={formData.description ?? ""}
            callback={handleChange}
          />
        </div>
        <div className="col-6">
          <CalendarInput
            name="date"
            placeholder={t(
              action === "ACTION" ? "Date Action Completed" : "Reminder Date"
            )}
            value={formData.date}
            callback={handleChange}
            minDate={false}
            className="w100"
          />
        </div>
        <div className="col-12">
          <TextArea
            name="comment"
            placeholder={t("Comment")}
            rows={4}
            value={formData.comment ?? ""}
            callback={handleChange}
          />
        </div>
      </div>
      <div className="w-100 d-flex justify-content-center mt-4">
        <Button onClick={handleSubmit} disabled={!isValid}>
          {t("Validate")}
        </Button>
      </div>
      {!UPDATE && (
        <button
          className="border-0 bg-transparent px-1 text-dark position-absolute top-0 end-0 f24"
          onClick={handleClose}
        >
          <i className="bi bi-x" />
        </button>
      )}
    </div>
  );
};

const ProjectAction = ({ projectId, data, isOpen, setIsOpen }) => {
  const { t } = useTranslation("common");
  const dispatch = useDispatch();
  const { status, date, comment, description, type, isReminder } = data;

  const options = [
    {
      text: isReminder ? t("Edit Reminder") : t("Edit Action"),
      callback: () =>
        dispatch(
          openCenteredModal(
            <ProjectActionForm
              projectId={projectId}
              status={data.status}
              action={isReminder ? "REMINDER" : "ACTION"}
              data={data}
              handleClose={() => dispatch(closeCenteredModal())}
            />
          )
        ),
    },
    ...(isReminder
      ? [
          {
            text: t("Convert to Action"),
            callback: () =>
              dispatch(
                openCenteredModal(
                  <ConfirmModal
                    text="Are you sure you want to convert this reminder to an action"
                    confirmText={t("Validate")}
                    onConfirm={() =>
                      dispatch(
                        convertProjectReminderToAction(data.id, projectId, t)
                      )
                    }
                  />
                )
              ),
          },
        ]
      : []),
    {
      text: isReminder ? t("Delete Reminder") : t("Delete Action"),
      red: true,
      callback: () =>
        dispatch(
          openCenteredModal(
            <ConfirmModal
              id={data.id}
              label={t(`${isReminder ? "Reminder" : "Action"}`)}
              onConfirm={() => dispatch(deleteProjectAction(data.id, projectId, t))}
            />
          )
        ),
    },
  ];

  return (
    <div
      className="cursor-pointer position-relative py-4 ps-4 pe-5"
      onClick={() => setIsOpen(isOpen === data.id ? null : data.id)}
    >
      <div className="d-flex align-items-center gap-4">
        <div
          className={`position-absolute br5 z-n1 top-0 start-0 w-100 h-100 ${
            isOpen === data.id
              ? isReminder
                ? "bg-bronze-400"
                : "bg-blue-300"
              : ""
          }`}
        ></div>
        <ActionIcon isReminder={isReminder} type={type} />
        <div className="d-flex flex-column">
          <p className="f13 text-ultraBlack mb-0 medium">
            {projectStatusLabel(status, t)}
          </p>
          <p className="f13 text-ultraBlack mb-0 medium">
            <span className="text-uppercase">
              {date ? (
                <span className="bold">{`${moment(date).format(
                  "DD/MM/YYYY"
                )} -`}</span>
              ) : (
                ""
              )}{" "}
              {isReminder ? `${t("Reminder")} - ` : ""}
              {t(`${type ?? ""}`)}{" "}
              {!isReminder && description?.length > 0 ? `- ${description}` : ""}
            </span>
          </p>
        </div>
      </div>
      {isOpen === data.id && (
        <>
          {comment?.length > 0 && (
            <div className="d-flex flex-column f14 ps-5 ms-4 text-ultraBlack mt-2">
              <span>{t("Comment Action")} :</span>
              <p className="mb-0">{comment}</p>
            </div>
          )}
          <div className="position-absolute top-0 end-0 me-2 mt-3">
            <VerticalPoints showLeft options={options} />
          </div>
        </>
      )}
    </div>
  );
};

const ProjectActions = ({ project }) => {
  const { t } = useTranslation("common");
  const dispatch = useDispatch();
  const [status, setStatus] = useState(
    project.status
      ? { name: projectStatusLabel(project.status, t), code: project.status }
      : projectStatusOptions(t)[0]
  );
  const [newAction, setNewAction] = useState();
  const [isOpen, setIsOpen] = useState();

  const handleStatusChange = (option) => {
    setStatus(option);
    if (option?.code && option.code !== project.status) {
      dispatch(updateProject(project.id, { status: option.code }));
    }
  };

  return (
    <div className="flex-1 d-flex flex-column">
      <div className="modalContent pt-4 pb-5">
        <div className="mb-4 max-w-300px px-5">
          <p className="f16 text-ultraBlack mb-2">{t("Status")}</p>
          <Select
            name="status"
            value={status}
            placeholder={t("Status")}
            options={projectStatusOptions(t)}
            appendToSelf
            callback={(e) => handleStatusChange(e.target.value)}
          />
        </div>
        {project.actions?.length > 0 && (
          <div className="d-flex flex-column mt-5 position-relative pt-4 px-4">
            <span className="position-absolute top-0 ms-5 start-22 br5 h-100-25px border border-gray-100 w-6px" />
            {project.actions.map((action) => (
              <ProjectAction
                key={action.id}
                projectId={project.id}
                data={action}
                isOpen={isOpen}
                setIsOpen={setIsOpen}
              />
            ))}
          </div>
        )}
        <Separator className="my-4 mx-5" />
        <div className="px-5">
          {newAction ? (
            <ProjectActionForm
              projectId={project.id}
              status={status?.code}
              action={newAction}
              handleClose={() => setNewAction()}
            />
          ) : (
            <div className="d-flex gap-4">
              <Button onClick={() => setNewAction("ACTION")}>
                {t("Add an action")}
              </Button>
              <Button onClick={() => setNewAction("REMINDER")}>
                {t("Add a reminder")}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectActions;
